import { pdfToImages } from "~/fc/Helpers/PdfHelper";
import { loadScript } from "~/fc/Helpers/DOMHelper";
import { SORTABLE_JS_CDN, PDF_JS_CDN } from "~/config/external-packages";
import { THUMB_ROTATION_OPTIONS } from '~/config/thumb.config'
import { uuidv4 } from "~/fc/Helpers/FileHelpers";
import MergeOptionHandler from "../AdvacedSettings/MergeOptionHandler";
import Job from "../Converter/job";


/**
 * Extracts the numeric range and rotation direction from a given range string.
 *
 * @param {string} rangeString - The input string containing a numeric range and an optional rotation direction.
 * @returns {{range: string, rotation: string}} An object containing the numeric range and the rotation direction.
 * - `range` (string): The numeric range extracted from the input string (e.g., '1', '1-2', '1-2odd', '1-2even').
 * - `rotation` (string): The rotation direction (e.g., 'south', 'east', 'west') if present, otherwise an empty string.
 */
export const getRangeAndRotationFromRangeString = (rangeString) => {
    // Extract rotation from range if present
    let rotation = ''
    const directions = ['south', 'east', 'west'];
    for (const dir of directions) {
        if (rangeString.endsWith(dir)) {
            rotation = dir
        }
    }

    // Remove rotation from the string to get the numeric range part
    const range = rotation ? rangeString.slice(0, -rotation.length) : rangeString;
    
    return {
        range,
        rotation
    }
}

/**
 * Generates thumb preview elements for files.
 * @param {*} files 
 * @returns 
 */
export const extractThumbnailFromFiles = async (files) => {
    if (!files.every(file => file.file)) return;
    const pdfFiles = files.filter(file => file.ext === 'pdf');
    const imageFiles = files.filter(file => file.ext !== 'pdf');
    const errors = []
    const currentFilesLength = $nuxt.$store.state.thumb?.thumbs?.length || 0;
    
    const thumbsFromPdfFiles = await Promise.allSettled(
        pdfFiles.map(async (file, index) => {
            try {
                const images = await pdfToImages(file) || []
                const backgroundColor = generateLightSoftColor(currentFilesLength + index);
                
                return images.map((image, index) => ({
                    thumbId: uuidv4(),
                    preview: image,
                    page: index + 1,
                    backgroundColor: backgroundColor,
                    name: file.name,
                    ext: file.ext,
                    total: file.total,
                    fileId: file.id,
                    include: true,
                    rotation: file.rotation || 0,
                    partition: file.partition || 0,
                }));
            } catch (error) {
                errors.push({
                    file: file,
                    error: error
                })
                return null; // Return null or some error indicator
            }
        })
    );

    // if (errors.length > 0) {
    //     errors.forEach(({file, error}) => {
    //         $nuxt.$store.dispatch("removeFile", file.id)
    //         (new ToastManager($nuxt).add('danger', '', `Error: ${file.name} file is broken, please recheck the file.`))            
    //     })
    // }

    const thumbsFromImageFiles = imageFiles.map(file => ({
        preview: getUrl(file),
        name: file.name,
        ext: file.ext,
        total: file.total,
        fileId: file.id
    }));

    const extractedThumbnails = [
        ...JSON.parse(JSON.stringify(thumbsFromPdfFiles.map(files => files.value).flat().filter(Boolean))),
        ...thumbsFromImageFiles
    ];

    return extractedThumbnails.map((thumb, index) => ({
        ...thumb,
        thumbIndex: (($nuxt.$store.state.thumb.thumbs?.length || 0) + 1) + index
    }));
};

/**
 * Groups an array of files by `fileId` if adjacent files share the same `fileId`.
 * Copies properties from the first child to the parent group.
 *
 * @param {Array} thumbs - Array of file objects with properties: page, backgroundColor, name, ext, total, fileId, and optional preview.
 * @returns {Array} - Array of grouped objects with properties: fileId, name, ext, total, backgroundColor, preview, and children.
 */
export const groupAdjacentFilesByFileId = (thumbs) => {
    const grouped = [];
    let currentGroup = null;
    
    for (const thumb of thumbs) {
        if (currentGroup && currentGroup.fileId === thumb.fileId && currentGroup.partition === thumb.partition) {
            // Add to current group
            currentGroup.children.push({ ...thumb });
            // If any file is included, the group should be displayed as included
            if (thumb.include) currentGroup.include = true;
        } else {
            // Start a new group (or split when partition changes)
            currentGroup = {
                fileId: thumb.fileId,
                partition: thumb.partition, // Ensure partition is considered
                name: thumb.name,
                thumbId: thumb.thumbId || uuidv4(),
                ext: thumb.ext,
                total: thumb.total,
                backgroundColor: thumb.backgroundColor,
                preview: thumb.preview || null,
                include: thumb.include,
                rotation: thumb.rotation,
                children: [thumb]
            };
            grouped.push(currentGroup);
        }
    }

    return grouped;
};


/**
 * Flattens a grouped array of files back to its original format.
 *
 * @param {Array} groupedFiles - Array of grouped file objects, each containing a `children` array.
 * @returns {Array} - Original flat array of file objects.
 */
export const flattenGroupedFiles = (groupedFiles) => {
    return groupedFiles.map(file => (file.children || [])).flat()
}

/**
 * File rotation logic base on the direction and current rotate value.
 * @param {*} page 
 * @param {*} isClockWise 
 * @returns 
 */
export const rotateFile = (page, isClockWise) => {
    const rotations = THUMB_ROTATION_OPTIONS
    const index = rotations.indexOf(page.rotation) === -1 ? 0 : rotations.indexOf(page.rotation)
    const rotation = isClockWise
        ? index === 3
            ? rotations[0] : rotations[index + 1]
        : index === 0
            ? rotations[3] : rotations[index - 1]
    return {
        ...page,
        rotation
    }
}

/**
 * Generates Data URL for given File
 * @param {*} file 
 * @returns 
 */
const getUrl = (file) => {
    if (file.file === 'url') { return file.src }
    return URL.createObjectURL(file ? file.file : new Blob())
}

/**
 * Selects a color from a preselected color list
 * @returns {string} A hex color code.
 */
const generateLightSoftColor = (index) => {
    const colors = ['#F5C8D5', '#F5E3B1', '#ADE6ED', '#EECAF6', '#BCDAFC', '#A9EBC7', '#F2B9C0', '#F6D3B3', '#CDC9FB', '#A9C6FE', '#C7CAD1', '#ADE6ED'];
    return colors[index % colors.length];
}

/**
 * Loads up sortable js library via CDN and initiates the UI
 * @param {*} callback 
 */
export const loadSortable = (callback) => {
    if (window.Sortable) callback()
    else loadScript(SORTABLE_JS_CDN, "sortable",callback())
    // else loadScript(SORTABLE_JS_CDN, "sortable", () => {
    //     loadScript(VUE_DRAGGABLE_CDN, "VueDraggble", callback())
    // })
}

/**
 * Loads up PdfJs library via CDN and initiates the UI
 * @param {*} callback 
 */
export const loadPdfJS = (callback) => {
    if (window.pdfjsLib) callback
    else loadScript(PDF_JS_CDN, "pdfjs", callback)
}

const generateImportTasks = (list) => {
    const importTasks = {}
    const uploadTasks = {}

    list.forEach((importTask, i) => {
        if(importTask.importTaskId || importTask.import) return 
        if (importTask.file === 'url') {
            if (importTask.gtoken) {
                importTasks['import-' + i] = {
                    operation: 'import/google-drive',
                    fileId: importTask.url,
                    gtoken: importTask.gtoken,
                    filename: importTask.name,
                    filesize: importTask.total
                }
            } else {
                if (importTask.name) {
                    importTasks['import-' + i] = {
                        operation: 'import/url',
                        url: importTask.url,
                        filename: importTask.name
                    }
                } else {
                    importTasks['import-' + i] = {
                        operation: 'import/url',
                        url: importTask.url
                    }
                }
            }
        } else {
            importTasks['import-' + i] = { operation: 'import/upload' }
            uploadTasks['import-' + i] = importTask
        }
    })

    return {
        importTasks,
        uploadTasks
    }
}

const createImportTasksJob = async (list) => {
    const { importTasks, uploadTasks } = generateImportTasks(list);
    if (Object.keys(importTasks).length === 0) {
        return {}
    }
    const job = new Job(importTasks)
    return await job.processBulkFiles({ uploads: uploadTasks, jobId: uuidv4(), returnJob: true })
}

/**
 * We have a few thumb/preview based convert
 * tools.
 */
export const prepareFilesForThumbBasedConversion = async ($next) => {
    const allThumbs = $next.$store.state.thumb.thumbs
    const info = $next.$store.state.info
    const list = $next.$store.state.list
    
    await $next.$store.dispatch('thumb/generateRangeString')
    if(info.slug === 'merge-pdf') new MergeOptionHandler().setMerge(true)
    
    if(info.slug === 'split-pdf') {
        // create a job containing only import tasks for all the files
        // and map the created taskId to file
        const job =  await createImportTasksJob(list)
        if (Array.isArray(job.tasks)) {
            job.tasks.forEach(task => {
                if (task.name) {
                    // during task creation, we keep the index of file like import-0 or import-1
                    const idx = task.name.split('-').length > 0 ? task.name.split('-')[1] : -1
                    if (idx !== -1) {
                        list[idx].importTaskId = task.id
                    }
                }
            })
        }
        
        const partitions = Array.from(new Set(allThumbs?.map(thumb => thumb.partition))).filter(x => x !== -1)
        const allRanges = $next.$store.state.thumb.ranges ? Object.entries($next.$store.state.thumb.ranges).map(([key, value]) => {
            let [partition, fileId] = key.split(':')
            fileId = fileId.includes('_') ? fileId.split('_')[0] : fileId
            return { partition, fileId, range: value }
        }) : null; 
        const fileList = partitions.map(partition => {
            const thumbs = structuredClone(allThumbs).filter(thumb => thumb.partition === partition)
            const ranges = structuredClone(allRanges).filter(range => range.partition == partition)
            const files = ranges.map(range => list.find(item => item.id == range.fileId))
            return {
                partition, thumbs, ranges, files,
                id: uuidv4(),
                operation: 'merge',
                name: `PDF_${((partition+1) < 10) ? `0${partition+1}` : (partition+1)}`
            }
        })
        $next.$store.commit("setReloadedList", list)
        $next.$store.commit("setList", fileList)
        $next.$store.commit("setFileInputConcurrentLength", fileList.length)
    }
}

